/**
 * @module csvUtils
 * Pure utility functions for CSV generation and download.
 * Follows RFC 4180 standard for field escaping.
 */

const CSV_COLUMNS = [
  { key: "zone_id", header: "Zone ID" },
  { key: "zone_name", header: "Zone Name" },
  { key: "zone_type", header: "Zone Type" },
  { key: "square_footage", header: "Square Footage" },
  { key: "capacity", header: "Capacity" },
  { key: "rooms", header: "Rooms" }
]

const FORMULA_TRIGGERS = new Set(["=", "+", "-", "@", "\t", "\r"])

/**
 * Escapes a value for CSV output per RFC 4180.
 * Arrays are joined with ", " and the result is escaped as a string.
 * Sanitizes formula-triggering characters to prevent CSV injection in spreadsheet apps.
 *
 * @param {*} value - The value to escape
 * @returns {string} CSV-safe string
 */
export function escapeCSVField(value) {
  if (value == null || value === "") return ""
  if (Array.isArray(value)) {
    if (value.length === 0) return ""
    const joined = value.map(item => String(item)).join(", ")
    return escapeCSVField(joined)
  }
  const str = String(value)
  const needsQuoting = str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")
  const needsSanitizing = FORMULA_TRIGGERS.has(str[0])
  if (needsSanitizing) {
    const safe = "'" + str
    return `"${safe.replace(/"/g, '""')}"`
  }
  if (needsQuoting) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/**
 * Converts an array of zone objects to a CSV string.
 * Sorts zones by zone_name for consistent, user-friendly output.
 *
 * @param {Array<Object>} zones - Zone data objects from output.json
 * @returns {string} RFC 4180 compliant CSV string with CRLF line endings
 */
export function zonesToCSV(zones) {
  const sorted = [...zones].sort((a, b) => {
    const nameA = (a.zone_name || "").toLowerCase()
    const nameB = (b.zone_name || "").toLowerCase()
    return nameA.localeCompare(nameB)
  })
  const header = CSV_COLUMNS.map(col => col.header).join(",")
  const rows = sorted.map(zone =>
    CSV_COLUMNS.map(col => escapeCSVField(zone[col.key])).join(",")
  )
  return [header, ...rows, ""].join("\r\n")
}

/**
 * Generates a filename for CSV download.
 * Format: {school-name-slug}-zone-data-upload-{YYYY-MM-DD-HH-mm}.csv
 *
 * @param {string|null} schoolName - School display name
 * @returns {string} Filename string
 */
export function generateCSVFilename(schoolName) {
  const now = new Date()
  const timestamp = [
    now.getUTCFullYear(),
    String(now.getUTCMonth() + 1).padStart(2, "0"),
    String(now.getUTCDate()).padStart(2, "0"),
    String(now.getUTCHours()).padStart(2, "0"),
    String(now.getUTCMinutes()).padStart(2, "0")
  ].join("-")

  const slug = (schoolName || "")
    .toLowerCase()
    .replace(/['']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")

  const prefix = slug ? `${slug}-` : ""
  return `${prefix}zone-data-upload-${timestamp}.csv`
}

/**
 * Triggers a browser download of CSV content.
 *
 * @param {string} csvContent - The CSV string to download
 * @param {string} filename - The filename for the download
 */
export function downloadCSV(csvContent, filename) {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.style.display = "none"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
